import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAuth, signOut } from 'firebase/auth';
import { clearPassedOnboarding } from '../utils/userSelections';
import { sendContactMessage } from '../utils/contactUs';
// import { useShowPaymentsFlag } from 'app/utils/useShowPaymentsFlag';
import { firebaseApp } from '../utils/firebaseConfig';
import { requestFlutterSignOut } from './useFlutterGoogleAuth';
import { getChildrenForParent } from '../utils/parentStudentFirestore';
import iconParentChild from '../assets/webp/icon-parent-child.webp';
import iconPayments from '../assets/webp/icon-payments.webp';
import iconBirthdate from '../assets/webp/icon-birthdate.webp';
import iconParasha from '../assets/webp/icon-parasha.webp';
import iconTradition from '../assets/webp/icon-tradition.webp';
import iconSettings from '../assets/webp/icon-settings.webp';
import iconRabbi from '../assets/webp/icon-rabbi.webp';
import iconFaq from '../assets/webp/icon-faq.webp';
import iconContact from '../assets/webp/icon-contact.webp';
import { SUPPORTED_LANGUAGES } from './LanguageSelector';

// Color constants (matching theme)
const BLUE_ACCENT = '#3B7BF4';
const TEXT_DARK = '#1A1A2E';
const TEXT_SECONDARY = '#6B7280';
const BORDER_LIGHT = '#D1D5DB';

interface HamburgerMenuProps {
  className?: string;
}

interface StoredUser {
  userId?: string | null;
  email: string | null;
  name?: string | null;
  displayName?: string | null;
  photoURL?: string | null;
}

const getInitials = (name: string): string => {
  const trimmed = name.trim();
  if (!trimmed) return '';
  const parts = trimmed.split(' ').filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return trimmed[0].toUpperCase();
};

const HamburgerMenu: React.FC<HamburgerMenuProps> = ({ className }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const loggedInUser = (() => {
    try {
      const raw = localStorage.getItem('loggedInUser');
      return raw ? (JSON.parse(raw) as StoredUser) : null;
    } catch {
      return null;
    }
  })();
  const adminEmails = [
    'shimibis@gmail.com',
    'shaul.almog.test3@gmail.com',
    'shaul.almog@gmail.com',
    'david.cohen@example.com',
  ];
  const userName: string =
    loggedInUser?.displayName || loggedInUser?.name || loggedInUser?.email || '';
  const showPaymentsInMenu = true;

  const isAdmin = adminEmails.includes(loggedInUser?.email || '');

  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isConnectedParent, setIsConnectedParent] = useState(false);

  useEffect(() => {
    const email = loggedInUser?.email;
    if (!email) return;
    getChildrenForParent(email)
      .then((links) => {
        if (links.length > 0) setIsConnectedParent(true);
      })
      .catch(() => {
        /* silent */
      });
  }, [loggedInUser?.email]);

  // Always use RTL for menu, regardless of language
  const direction = 'rtl';

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setShowContactForm(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when menu is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleSignOut = useCallback(async () => {
    try {
      const auth = getAuth(firebaseApp);
      await signOut(auth);
    } catch (e) {
      console.warn('Firebase signOut error (continuing with local sign-out):', e);
    }
    // Tell Flutter to sign out from Google/Apple so next sign-in shows account picker
    await requestFlutterSignOut();
    localStorage.removeItem('loggedInUser');
    localStorage.removeItem('selectedRabbi');
    localStorage.removeItem('rabbiToken');
    localStorage.removeItem('rabbiInfo');
    clearPassedOnboarding();
    setIsOpen(false);
    navigate('/');
    window.location.reload();
  }, [navigate]);

  const handleChooseTradition = useCallback(() => {
    setIsOpen(false);
    navigate('/tradition');
  }, [navigate]);

  const handleChooseParasha = useCallback(() => {
    setIsOpen(false);
    navigate('/choose-parasha');
  }, [navigate]);

  const handleChooseBirthdate = useCallback(() => {
    setIsOpen(false);
    navigate('/aliya-latora');
  }, [navigate]);

  const handleRabbiLogin = useCallback(() => {
    setIsOpen(false);
    navigate('/rabbi-login');
  }, [navigate]);

  const handleConnectRabbi = useCallback(() => {
    setIsOpen(false);
    navigate('/connect-rabbi');
  }, [navigate]);

  // const handleRegisterRabbi = useCallback(() => {
  //   setIsOpen(false);
  //   navigate("/register-rabbi-request");
  // }, [navigate]);

  const handleChooseLanguage = useCallback(() => {
    setIsOpen(false);
    navigate('/choose-language', { state: { from: location.pathname } });
  }, [navigate, location.pathname]);

  const handleSettings = useCallback(() => {
    setIsOpen(false);
    navigate('/settings');
  }, [navigate]);

  const handleFAQ = useCallback(() => {
    setIsOpen(false);
    navigate('/faq');
  }, [navigate]);

  const handleContactUs = useCallback(() => {
    setShowContactForm(true);
  }, []);

  const handlePayments = useCallback(() => {
    setIsOpen(false);
    navigate('/payments');
  }, [navigate]);

  const handleChildProgress = useCallback(() => {
    setIsOpen(false);
    navigate('/parent-progress');
  }, [navigate]);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await sendContactMessage(contactForm);

      setSubmitStatus('success');
      setTimeout(() => {
        setShowContactForm(false);
        setContactForm({ name: '', email: '', message: '' });
        setSubmitStatus('idle');
      }, 2000);
    } catch (error) {
      console.error('EmailJS error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const menuIcon = (src: string) => <img src={src} alt="" style={{ height: 28 }} />;

  const currentLanguageFlag =
    SUPPORTED_LANGUAGES.find((lang) => i18n.language?.startsWith(lang.code))?.flag ?? '🌐';

  const menuItems = [
    {
      id: 'choose-language',
      icon: <span style={{ fontSize: 24, lineHeight: '28px' }}>{currentLanguageFlag}</span>,
      label: t('menu_choose_language', 'Choose Language'),
      onClick: handleChooseLanguage,
      color: BLUE_ACCENT,
    },
    {
      id: 'parent-child-connection',
      icon: menuIcon(iconParentChild),
      label: t('parent_child_connection', 'חיבור הורה - ילד'),
      onClick: () => navigate('/choose-parent-child'),
      color: BLUE_ACCENT,
    },
    ...(showPaymentsInMenu
      ? [
          {
            id: 'payments',
            icon: menuIcon(iconPayments),
            label: t('menu_payments', 'Subscriptions'),
            onClick: handlePayments,
            color: BLUE_ACCENT,
          },
        ]
      : []),
    {
      id: 'choose-birthdate',
      icon: menuIcon(iconBirthdate),
      label: t('menu_choose_birthdate', 'Choose Birthdate'),
      onClick: handleChooseBirthdate,
      color: BLUE_ACCENT,
    },
    {
      id: 'choose-parasha',
      icon: menuIcon(iconParasha),
      label: t('menu_choose_parasha', 'Choose Parasha'),
      onClick: handleChooseParasha,
      color: BLUE_ACCENT,
    },
    {
      id: 'choose-tradition',
      icon: menuIcon(iconTradition),
      label: t('menu_choose_tradition', 'Choose Tradition'),
      onClick: handleChooseTradition,
      color: BLUE_ACCENT,
    },
    {
      id: 'settings',
      icon: menuIcon(iconSettings),
      label: t('menu_settings', 'Settings'),
      onClick: handleSettings,
      color: BLUE_ACCENT,
    },
    {
      id: 'rabbi-login',
      icon: menuIcon(iconRabbi),
      label: t('menu_rabbi_login', 'Rabbi Login'),
      onClick: handleRabbiLogin,
      color: BLUE_ACCENT,
      adminOnly: true,
    },
    {
      id: 'connect-rabbi',
      icon: menuIcon(iconRabbi),
      label: t('menu_connect_rabbi', 'Connect to my Rabbi'),
      onClick: handleConnectRabbi,
      color: BLUE_ACCENT,
    },
    // {
    //   id: 'connect-parent',
    //   icon: '🔗',
    //   label: t('menu_connect_parent', 'Connect to my parent'),
    //   onClick: handleConnectParent,
    //   color: BLUE_ACCENT,
    //   adminOnly: true,
    // },
    // {
    //   id: 'connect-child',
    //   icon: '👨‍👩‍👧',
    //   label: t('menu_connect_child', 'Connect to my child'),
    //   onClick: handleConnectChild,
    //   color: BLUE_ACCENT,
    //   adminOnly: true,
    // },
    ...(isConnectedParent
      ? [
          {
            id: 'child-progress',
            icon: '📊',
            label: t('menu_child_progress', "Child's Progress"),
            onClick: handleChildProgress,
            color: BLUE_ACCENT,
          },
        ]
      : []),
    {
      id: 'faq',
      icon: menuIcon(iconFaq),
      label: t('menu_faq', 'שאלות נפוצות'),
      onClick: handleFAQ,
      color: BLUE_ACCENT,
    },
    {
      id: 'contact-us',
      icon: menuIcon(iconContact),
      label: t('menu_contact_us', 'Contact Us'),
      onClick: handleContactUs,
      color: BLUE_ACCENT,
    },
  ];

  return (
    <>
      {/* Hamburger Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={className}
        style={{
          width: 36,
          height: 36,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 5,
          background: '#DCE9F9',
          border: `1px solid ${BORDER_LIGHT}`,
          borderRadius: 10,
          cursor: 'pointer',
          padding: 6,
          transition: 'all 0.2s ease',
        }}
        aria-label={t('menu_open', 'Open menu') as string}
        aria-expanded={isOpen}
      >
        <span
          style={{
            display: 'block',
            width: 18,
            height: 2,
            background: TEXT_DARK,
            borderRadius: 1,
            transition: 'all 0.3s ease',
          }}
        />
        <span
          style={{
            display: 'block',
            width: 18,
            height: 2,
            background: TEXT_DARK,
            borderRadius: 1,
            transition: 'all 0.3s ease',
          }}
        />
        <span
          style={{
            display: 'block',
            width: 18,
            height: 2,
            background: TEXT_DARK,
            borderRadius: 1,
            transition: 'all 0.3s ease',
          }}
        />
      </button>

      {/* Overlay */}
      {isOpen && (
        <div
          onClick={() => {
            setIsOpen(false);
            setShowContactForm(false);
          }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999,
            opacity: isOpen ? 1 : 0,
            transition: 'opacity 0.3s ease',
          }}
          aria-hidden="true"
        />
      )}

      {/* Sliding Menu Panel - Always opens from right */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '85%',
          maxWidth: 320,
          height: '100%',
          background: '#ffffff',
          zIndex: 1000,
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: isOpen ? '-4px 0 20px rgba(0, 0, 0, 0.15)' : 'none',
          display: 'flex',
          flexDirection: 'column',
          direction,
        }}
        role="dialog"
        aria-modal="true"
        aria-label={t('menu_title', 'Menu') as string}
      >
        {/* Menu Header */}
        <div
          style={{
            padding: '32px 24px 20px',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 10,
            position: 'relative',
          }}
        >
          {/* Close button – top left */}
          <button
            onClick={() => {
              setIsOpen(false);
              setShowContactForm(false);
            }}
            style={{
              position: 'absolute',
              top: 16,
              left: 16,
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(107, 114, 128, 0.1)',
              border: 'none',
              borderRadius: '50%',
              cursor: 'pointer',
              fontSize: 18,
              color: TEXT_SECONDARY,
            }}
            aria-label={t('menu_close', 'Close menu') as string}
          >
            ✕
          </button>

          {/* Avatar */}
          <div
            style={{
              width: 50,
              height: 50,
              borderRadius: '50%',
              overflow: 'hidden',
              background: BLUE_ACCENT,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {loggedInUser?.photoURL ? (
              <img
                src={loggedInUser.photoURL}
                alt={userName}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <span style={{ color: 'white', fontSize: 28, fontWeight: 800 }}>
                {getInitials(userName) || 'D'}
              </span>
            )}
          </div>

          {/* Name */}
          <span style={{ fontSize: 20, fontWeight: 800, color: TEXT_DARK }}>{userName}</span>

          {/* Sign-out button */}
        </div>

        {/* Menu Content */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '12px 16px',
          }}
        >
          {!showContactForm ? (
            // Menu Items
            <nav>
              <ul
                style={{
                  listStyle: 'none',
                  margin: 0,
                  padding: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                {(() => {
                  const visible = menuItems.filter((item) => !item.adminOnly || isAdmin);
                  return visible.map((item) => {
                    const isDelete = item.id === 'delete-account';
                    return (
                      <li key={item.id}>
                        <button
                          onClick={item.onClick}
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'start',
                            justifyContent: 'start',
                            gap: 8,
                            padding: '8px 20px',
                            background: isDelete ? 'rgba(239,68,68,0.07)' : '#ffffff',
                            border: isDelete
                              ? '1px solid rgba(239,68,68,0.25)'
                              : '0.5px solid #D9D9D9',
                            borderRadius: 50,
                            cursor: 'pointer',
                            fontSize: 17,
                            fontWeight: 600,
                            color: isDelete ? '#ef4444' : TEXT_DARK,
                            transition: 'background 0.2s ease, border-color 0.2s ease',
                            textAlign: 'center',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = isDelete
                              ? 'rgba(239,68,68,0.12)'
                              : '#F9FAFB';
                            e.currentTarget.style.borderColor = isDelete
                              ? 'rgba(239,68,68,0.5)'
                              : '#D1D5DB';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = isDelete
                              ? 'rgba(239,68,68,0.07)'
                              : '#ffffff';
                            e.currentTarget.style.borderColor = isDelete
                              ? 'rgba(239,68,68,0.25)'
                              : '#E5E7EB';
                          }}
                        >
                          <span style={{ display: 'flex', alignItems: 'center' }}>{item.icon}</span>
                          <span>{item.label}</span>
                        </button>
                      </li>
                    );
                  });
                })()}
              </ul>
              <button
                onClick={() => void handleSignOut()}
                style={{
                  padding: '8px 20px',
                  background: '#ff5d5d',
                  color: 'white',
                  border: 'none',
                  borderRadius: 20,
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: 20,
                  gap: 6,
                }}
              >
                {t('menu_sign_out', 'התנתק')}
                <span>→</span>
              </button>
            </nav>
          ) : (
            // Contact Form
            <div style={{ padding: '8px 0' }}>
              <button
                onClick={() => setShowContactForm(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  background: 'none',
                  border: 'none',
                  color: BLUE_ACCENT,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  marginBottom: 16,
                  padding: 0,
                }}
              >
                <span style={{ fontSize: 18 }}>→</span>
                {t('menu_back', 'Back')}
              </button>

              <h3
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: TEXT_DARK,
                  marginBottom: 16,
                }}
              >
                {t('contact_form_title', 'Contact Us')}
              </h3>

              {submitStatus === 'success' ? (
                <div
                  style={{
                    padding: 20,
                    background: '#f0fdf4',
                    borderRadius: 12,
                    textAlign: 'center',
                  }}
                >
                  <span style={{ fontSize: 40, display: 'block', marginBottom: 12 }}>✓</span>
                  <p style={{ color: '#166534', fontWeight: 600 }}>
                    {t('contact_form_success', 'Message sent successfully!')}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit}>
                  <div style={{ marginBottom: 14 }}>
                    <label
                      style={{
                        display: 'block',
                        fontSize: 13,
                        fontWeight: 600,
                        color: TEXT_SECONDARY,
                        marginBottom: 6,
                      }}
                    >
                      {t('contact_form_name', 'Name')}
                    </label>
                    <input
                      type="text"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        border: `1px solid ${BORDER_LIGHT}`,
                        borderRadius: 10,
                        fontSize: 15,
                        outline: 'none',
                        boxSizing: 'border-box',
                        transition: 'border-color 0.2s ease',
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = BLUE_ACCENT;
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = BORDER_LIGHT;
                      }}
                      placeholder={t('contact_form_name_placeholder', 'Your name') as string}
                    />
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <label
                      style={{
                        display: 'block',
                        fontSize: 13,
                        fontWeight: 600,
                        color: TEXT_SECONDARY,
                        marginBottom: 6,
                      }}
                    >
                      {t('contact_form_email', 'Email')}
                    </label>
                    <input
                      type="email"
                      value={contactForm.email}
                      onChange={(e) =>
                        setContactForm({
                          ...contactForm,
                          email: e.target.value,
                        })
                      }
                      required
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        border: `1px solid ${BORDER_LIGHT}`,
                        borderRadius: 10,
                        fontSize: 15,
                        outline: 'none',
                        boxSizing: 'border-box',
                        transition: 'border-color 0.2s ease',
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = BLUE_ACCENT;
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = BORDER_LIGHT;
                      }}
                      placeholder={t('contact_form_email_placeholder', 'your@email.com') as string}
                    />
                  </div>

                  <div style={{ marginBottom: 18 }}>
                    <label
                      style={{
                        display: 'block',
                        fontSize: 13,
                        fontWeight: 600,
                        color: TEXT_SECONDARY,
                        marginBottom: 6,
                      }}
                    >
                      {t('contact_form_message', 'Message')}
                    </label>
                    <textarea
                      value={contactForm.message}
                      onChange={(e) =>
                        setContactForm({
                          ...contactForm,
                          message: e.target.value,
                        })
                      }
                      required
                      rows={4}
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        border: `1px solid ${BORDER_LIGHT}`,
                        borderRadius: 10,
                        fontSize: 15,
                        outline: 'none',
                        boxSizing: 'border-box',
                        resize: 'vertical',
                        fontFamily: 'inherit',
                        transition: 'border-color 0.2s ease',
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = BLUE_ACCENT;
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = BORDER_LIGHT;
                      }}
                      placeholder={
                        t('contact_form_message_placeholder', 'How can we help you?') as string
                      }
                    />
                  </div>

                  {submitStatus === 'error' && (
                    <p
                      style={{
                        color: '#ef4444',
                        fontSize: 13,
                        marginBottom: 12,
                      }}
                    >
                      {t('contact_form_error', 'Failed to send. Please try again.')}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      width: '100%',
                      padding: '14px 20px',
                      background: BLUE_ACCENT,
                      color: 'white',
                      border: 'none',
                      borderRadius: 12,
                      fontSize: 16,
                      fontWeight: 600,
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      opacity: isSubmitting ? 0.7 : 1,
                      transition: 'opacity 0.2s ease, transform 0.2s ease',
                    }}
                  >
                    {isSubmitting
                      ? t('contact_form_sending', 'Sending...')
                      : t('contact_form_submit', 'Send Message')}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>

        {/* Menu Footer */}
        <div
          style={{
            padding: '16px 20px',
            borderTop: `1px solid ${BORDER_LIGHT}`,
            textAlign: 'center',
          }}
        >
          <p
            style={{
              fontSize: 12,
              color: TEXT_SECONDARY,
              margin: 0,
            }}
          >
            {t('menu_footer', 'Kol Torah © 2025')}
          </p>
        </div>
      </div>
    </>
  );
};

export default HamburgerMenu;
